import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type {
  AccessExtraScope, AccessExtraScopeBody, AccessHrRolePage, AccessOrgCode, AccessPersonEffect, AccessPersonEffectSummary,
  AccessPersonPage, AccessPersonSettingsBody,
} from '../api/types'
import type { Api } from '../api'
import { Badge, Button, MultiSearchSelect, Notice, SearchSelect, Segmented, type SearchSelectGroup, type SearchSelectOption } from '../components/ui'
import { ChangedMark, FilterSlot, HIERARCHY, Panel, PlaceChip, ReportGroups, ToggleChip, day, extraScopesOf, failureOf, messageOf } from './accessWords'

/*
 * WHAT CHANGES WHAT ONE PERSON SEES (05 section 7.4): their own settings on top of
 * their HR role, for their main company (Main scope), and extra scopes in other
 * companies. Every save is the whole state, previewed first by the same procedure
 * that saves (@DryRun), with a reason, quoting the version this page read.
 *
 * Laid out again 27 Oct 2026 (owner): each block on its own card; Main scope's
 * Review and Save in a bar held at the bottom right while the page scrolls; each
 * extra scope a card with its reports and their column groups as chips (D53).
 */

type Three = 'role' | 'on' | 'off'
type Line = AccessPersonSettingsBody['lines'][number] & {
  key: string
  paused?: string | null
  /** Saved for an earlier main company: kept as it is, gives nothing now. */
  old?: boolean
}

const MODE: Record<number, AccessPersonSettingsBody['hierarchy']> = { 0: 'off', 1: 'type1', 2: 'type2' }

/** The person's settings as the page read them, in the writer's terms. */
function settingsOf(page: AccessPersonPage) {
  const p = page.person!
  const own = (kind: 'REPORT' | 'GROUP') => Object.fromEntries(
    page.matrix.filter((c) => c.kind === kind && c.scopeKey === 0)
      .map((c) => [c.code, (c.source === 'PERSON_ON' ? 'on' : c.source === 'PERSON_OFF' ? 'off' : 'role') as Three]))
  const lines: Line[] = page.edits.map((e) => ({
    key: `e${e.editId}`,
    effect: e.effect === 'A' ? 'add' : 'remove',
    unit: e.anyUnit ? null : e.businessUnit,
    team: e.anyTeam ? null : e.businessTeam,
    resume: false,
    // Every stored line goes back with its own company: the writer finds the same
    // row by it, whether or not Get_Person counts the company as main (review, 26 Oct 2026).
    company: e.compId,
    paused: e.pausedUtc,
    old: !e.inMainCompany,
  }))
  return {
    reports: own('REPORT'),
    groups: own('GROUP'),
    hierarchy: (p.hierarchyFrom === 'PERSON' ? MODE[p.hierarchyMode] ?? 'off' : 'role') as AccessPersonSettingsBody['hierarchy'],
    phone: (p.mobileFrom === 'PERSON' ? (p.mobileAllowed ? 'on' : 'off') : 'role') as Three,
    lines,
  }
}
type Settings = ReturnType<typeof settingsOf>

// Lines compare in any order: a line put back (Undo, or a unit picked again) goes at the end,
// and an edit undone in full is no change (1 Nov 2026).
const comparable = (s: Settings) => JSON.stringify({ ...s, lines: [...s.lines].sort((a, b) => a.key.localeCompare(b.key)) })
const sameSettings = (a: Settings, b: Settings) => comparable(a) === comparable(b)

/* =========================================================================
   MAIN SCOPE
   ========================================================================= */

export function MainScopeTab({ api, page, onSaved }: { api: Api; page: AccessPersonPage; onSaved: (text: string) => void }) {
  const p = page.person!
  const read = useMemo(() => settingsOf(page), [page])
  const [draft, setDraft] = useState<Settings>(read)
  const [role, setRole] = useState<AccessHrRolePage | null>(null)
  // Until the role is read, "HR role says" says nothing rather than "Off".
  const [roleRead, setRoleRead] = useState(p.roleId === null)
  // Unread, which reports the role gives is not known, so no column group is placed (review of 18c).
  const [roleFailed, setRoleFailed] = useState(false)
  const [codes, setCodes] = useState<AccessOrgCode[] | null>(null)
  const lineSeq = useRef(0)
  // "No unit" ticked with none of its teams yet: nothing in the lines says so (19i).
  const [noUnitPicked, setNoUnitPicked] = useState(false)
  const [effect, setEffect] = useState<{ draft: Settings; answer: AccessPersonEffect } | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<{ tone: 'good' | 'bad'; text: string } | null>(null)

  useEffect(() => setDraft(read), [read])
  // What the HR role says, for the "HR role says" column.
  useEffect(() => {
    if (p.roleId === null) return
    let cancelled = false
    api.accessHrRole(p.roleId)
      .then((r) => { if (!cancelled) { setRole(r); setRoleRead(true); setRoleFailed(false) } })
      .catch(() => { if (!cancelled) { setRole(null); setRoleRead(true); setRoleFailed(true) } })
    return () => {
      cancelled = true
    }
  }, [api, p.roleId])
  // The D365 codes they may be given in their main company.
  useEffect(() => {
    if (!p.compId) return
    let cancelled = false
    api.accessOrgCodes(p.compId).then((c) => !cancelled && setCodes(c.codes)).catch(() => !cancelled && setCodes([]))
    return () => {
      cancelled = true
    }
  }, [api, p.compId])

  const changed = !sameSettings(draft, read)
  const reviewed = !!effect && sameSettings(effect.draft, draft)
  const roleSays = (kind: 'reports' | 'groups', code: string) =>
    !!role?.role?.setUp && (kind === 'reports'
      ? role.reports.some((x) => x.slug === code && x.allowed)
      : role.columnGroups.some((x) => String(x.groupId) === code && x.allowed))
  // How much a choice gives, for Added or Removed: on is 1 and off 0; "Same as role" is the role's.
  const onOff = (roleOn: () => boolean) => (v: string) => (v === 'role' ? (roleRead ? Number(roleOn()) : null) : v === 'on' ? 1 : 0)
  const hierarchyLevel = (v: string) => (v === 'role' ? (roleRead ? (role?.role?.setUp ? role.role.hierarchyMode ?? 0 : 0) : null)
    : v === 'type2' ? 2 : v === 'type1' ? 1 : 0)
  // Their main scope's reports as this draft makes them: the role's, less what they
  // switch off, plus what they switch on. A group shows under its own report only
  // (D53); one set while its report was theirs, whose report has since gone, is
  // kept and does nothing, and is shown as such.
  const reportOn = (slug: string) => draft.reports[slug] === 'on' || (draft.reports[slug] !== 'off' && roleSays('reports', slug))
  const groupInfo = (code: string) => page.matrix.find((c) => c.kind === 'GROUP' && c.code === code && c.scopeKey === 0)
  const reportLabel = (slug: string) => page.matrix.find((c) => c.kind === 'REPORT' && c.code === slug && c.scopeKey === 0)?.label ?? slug
  // A report switched out of their main scope takes its unsaved group changes with it
  // (review of 18c): only a setting saved as it is may stay on a report not theirs (51627).
  const setReport = (code: string, v: Three) =>
    setDraft((d) => {
      const next = { ...d, reports: { ...d.reports, [code]: v } }
      if (v === 'on' || (v !== 'off' && roleSays('reports', code))) return next
      const groups = { ...next.groups }
      for (const g of Object.keys(groups)) if (groupInfo(g)?.reportSlug === code) groups[g] = read.groups[g]
      return { ...next, groups }
    })
  const groupsPlaced = roleRead && !roleFailed

  const body = (): AccessPersonSettingsBody => ({
    expectedVersion: p.settingsVersion,
    reports: draft.reports,
    columnGroups: draft.groups,
    hierarchy: draft.hierarchy,
    phone: draft.phone,
    lines: draft.lines.map(({ effect: e, unit, team, resume, company }) => ({ effect: e, unit, team, resume, company })),
    reason: reason.trim() || undefined,
    columnGroupKeys: groupKeysOf(page, Object.keys(draft.groups)),
  })

  const review = () => {
    if (busy) return
    setBusy(true)
    setNotice(null)
    api.accessPersonSettingsPreview(p.mindzerUserId, body())
      .then((a) => setEffect({ draft, answer: a }))
      .catch((e: unknown) => setNotice({ tone: 'bad', text: messageOf(e) }))
      .finally(() => setBusy(false))
  }

  const save = () => {
    if (!reviewed || busy || !reason.trim()) return
    setBusy(true)
    api.accessPersonSettingsSave(p.mindzerUserId, body())
      .then((a) => {
        setEffect(null)
        setReason('')
        onSaved(`Saved their settings. ${personEffectSentences(a.summary).join(' ')}`)
      })
      .catch((e: unknown) => {
        const f = failureOf(e)
        setEffect(null)
        setNotice({ tone: 'bad', text: f.kind === 'error' && f.reason === 'CHANGED_SINCE_READ'
          ? 'Someone else changed their settings since this page was read. Reload this page; yours are not saved.' : messageOf(e) })
      })
      .finally(() => setBusy(false))
  }

  const setLines = (lines: Line[]) => setDraft((d) => ({ ...d, lines }))
  // Removed by a line in the main company: a line kept from an earlier company removes nothing
  // (review, 26 Oct 2026). A paused one still takes away: 049 honours a remove even when paused,
  // since it can only narrow (review, 1 Nov 2026).
  const takesAway = (kind: 'UNIT' | 'TEAM', code: string) => (l: Line) => l.effect === 'remove' && !l.old
    && (kind === 'UNIT' ? l.unit === code && l.team === null : l.team === code)
  const removedChip = (kind: 'UNIT' | 'TEAM', code: string) => draft.lines.find(takesAway(kind, code))
  // Taking away again what this edit put back brings back the saved line, not a second one.
  const removeChip = (kind: 'UNIT' | 'TEAM', code: string) =>
    setLines([...draft.lines, read.lines.find(takesAway(kind, code))
      ?? { key: `n${Date.now()}`, effect: 'remove', unit: kind === 'UNIT' ? code : null, team: kind === 'TEAM' ? code : null, resume: false, company: null }])
  /*
   * UNITS, THEN TEAMS (owner, 29 Oct 2026): two multi-selects showing what this person has added
   * in their main company. The Teams list holds only the teams of the units picked. A unit with
   * none of its teams picked gives the whole unit (a unit line, every team in it); picking teams in
   * it gives only those teams (a team line each, in that unit).
   *
   * "No unit" reaches the teams D365 records under no unit (owner, 29 Oct 2026): picked alone it
   * adds nothing (a line naming neither would be every unit); each of its teams ticked is a line
   * with that team and no unit.
   *
   * Only what the admin touches is rewritten (review of 19i): a unit ticked or unticked, or a unit
   * whose teams changed. A line saved before keeps its key and pause when picked again, and
   * a line kept from an earlier company, a "takes away" line and a line for every unit stay as
   * they are: a take-away on its HR chip above, the rest in the table below.
   */
  const SEP = '\t'
  const NO_UNIT = '\u0001no-unit'
  const liveAdds = draft.lines.filter((l) => l.effect === 'add' && !l.old && (l.unit !== null || l.team !== null))
  const unitOf = (l: Line) => l.unit ?? NO_UNIT
  const unitsPicked = [...new Set(liveAdds.map(unitOf)),
    ...(noUnitPicked && !liveAdds.some((l) => l.unit === null) ? [NO_UNIT] : [])]
  const teamsPicked = liveAdds.filter((l) => l.team !== null).map((l) => `${unitOf(l)}${SEP}${l.team}`)
  const inUnit = (u: string, l: Line) => l.effect === 'add' && !l.old && (l.unit !== null || l.team !== null) && unitOf(l) === u
  // A unit's lines as asked: the one already in the draft, else the one saved, else a new one.
  const lineFor = (u: string, team: string | null): Line => {
    const unit = u === NO_UNIT ? null : u
    const same = (l: Line) => l.effect === 'add' && !l.old && (l.unit ?? null) === unit && (l.team ?? null) === team
    return draft.lines.find(same) ?? read.lines.find(same)
      ?? { key: `n${++lineSeq.current}`, effect: 'add', unit, team, resume: false, company: null }
  }
  const setUnits = (next: string[]) => {
    let lines = draft.lines
    for (const u of unitsPicked.filter((x) => !next.includes(x))) lines = lines.filter((l) => !inUnit(u, l))
    for (const u of next.filter((x) => !unitsPicked.includes(x))) {
      if (u !== NO_UNIT) lines = [...lines, lineFor(u, null)]
    }
    setNoUnitPicked(next.includes(NO_UNIT))
    setLines(lines)
  }
  const setTeams = (next: string[]) => {
    let lines = draft.lines
    for (const u of unitsPicked) {
      const before = teamsPicked.filter((t) => t.startsWith(`${u}${SEP}`)).sort().join('\n')
      const after = next.filter((t) => t.startsWith(`${u}${SEP}`))
      if (before === [...after].sort().join('\n')) continue
      const target = after.length > 0
        ? after.map((t) => lineFor(u, t.slice(u.length + SEP.length)))
        : u === NO_UNIT ? [] : [lineFor(u, null)]
      lines = [...lines.filter((l) => !inUnit(u, l)), ...target]
    }
    // Unticking the last "No unit" team leaves "No unit" picked, as on the additional scope card (review, 30 Oct 2026).
    if (unitsPicked.includes(NO_UNIT)) setNoUnitPicked(true)
    setLines(lines)
  }
  // The pickers' rows, named as the additional scope card names them, code in grey (05 7.4: what is
  // already part of their Mindzer placement says so, briefly, so the name keeps its room). A value picked that the code list no longer offers is
  // still a row, named by its code, so a trigger never shows a raw key.
  const placed = new Set(page.placement.map((x) => x.d365Code).filter(Boolean))
  const inMindzer = (code: string | null) => (code && placed.has(code) ? ' · already theirs from HR' : '')
  const unitCodes = (codes ?? []).filter((c) => c.kind === 'UNIT' && c.unitCode)
  const teamCodes = (codes ?? []).filter((c) => c.kind === 'TEAM' && c.teamCode)
  const unitOptions = [
    ...unitCodes.map((c) => ({ value: c.unitCode!, label: c.mindzerName ?? c.unitCode!, hint: `${c.unitCode}${inMindzer(c.unitCode)}` })),
    ...(teamCodes.some((c) => !c.unitCode) || unitsPicked.includes(NO_UNIT)
      ? [{ value: NO_UNIT, label: 'No Business Unit', hint: 'Business Teams with no Business Unit' }] : []),
    ...unitsPicked.filter((u) => u !== NO_UNIT && !unitCodes.some((c) => c.unitCode === u))
      .map((u) => ({ value: u, label: u, hint: `${u} · not in use in D365 now` })),
  ]
  // With more than one unit picked, each team sits under its unit's heading (teamHeadings). A team
  // ticked under "No unit" is saved with no unit, which is every unit, so it says so (review, 30 Oct 2026).
  const teamOptions: SearchSelectOption[] = teamCodes.filter((c) => unitsPicked.includes(c.unitCode || NO_UNIT)).map((c) => ({
    value: `${c.unitCode || NO_UNIT}${SEP}${c.teamCode}`,
    label: c.mindzerName ?? c.teamCode!,
    hint: `${c.teamCode}${c.unitCode ? '' : ' · in any Business Unit'}${inMindzer(c.teamCode)}`,
    group: c.unitCode || NO_UNIT,
  }))
  for (const t of teamsPicked) {
    if (teamOptions.some((o) => o.value === t)) continue
    const [u, team] = t.split(SEP)
    // A team added with no unit counts in any unit it sits in, as on the additional scope card.
    const elsewhere = u === NO_UNIT ? teamCodes.find((c) => c.teamCode === team) : undefined
    teamOptions.push({ value: t, label: elsewhere?.mindzerName ?? team, hint: `${team} · ${elsewhere ? 'in any Business Unit' : 'not in use in D365 now'}`, group: u })
  }
  // A unit's lines are all paused; a unit has a line for all its teams, which an older page could save beside team lines.
  const pausedUnit = (u: string) => {
    const its = liveAdds.filter((l) => unitOf(l) === u)
    return its.length > 0 && its.every((l) => l.paused && !l.resume)
  }
  const wholeUnit = (u: string) => liveAdds.some((l) => unitOf(l) === u && l.team === null)
  const teamGroups = teamHeadings(unitOptions, unitsPicked, teamOptions, teamsPicked, NO_UNIT, wholeUnit, pausedUnit)
  // The list below is a table, one line a row (the manager, 1 Nov 2026: the sentences and the
  // summary line above them were hard to read). Each code with its Mindzer name, where it has one;
  // names are looked up by code alone (052's org code list), so a team's is the same in any unit,
  // else as HR's own chips above name it, so a take-away reads the same saved or not.
  const placedName = (code: string) => page.placement.find((x) => x.d365Code === code)?.name ?? undefined
  const unitName = (u: string) => unitCodes.find((c) => c.unitCode === u)?.mindzerName ?? placedName(u)
  const teamName = (t: string) => teamCodes.find((c) => c.teamCode === t)?.mindzerName ?? placedName(t)
  const noUnitTeamless = unitsPicked.includes(NO_UNIT) && !teamsPicked.some((t) => t.startsWith(`${NO_UNIT}${SEP}`))
  const label = (code: string) => page.matrix.find((c) => c.code === code && c.scopeKey === 0)?.label ?? code
  // Until saved, a saved line taken out stays in its place, struck through, with Undo (owner, 1 Nov
  // 2026: "it should show removed until i save"); a line not saved yet says Added, as removing it
  // only takes back this edit. Picked again, a unit brings back its saved line (lineFor).
  const rows: { line: Line; mark?: 'added' | 'removed' }[] = [
    ...read.lines.map((r) => {
      const now = draft.lines.find((l) => l.key === r.key)
      return now ? { line: now } : { line: r, mark: 'removed' as const }
    }),
    ...draft.lines.filter((l) => !read.lines.some((r) => r.key === l.key)).map((l) => ({ line: l, mark: 'added' as const })),
  ]
  // Undo of a unit's saved add also drops the lines this edit made in that unit that overlap it
  // (the whole unit beside its own teams), so the pickers still show what will be saved (review, 1 Nov 2026).
  const restore = (l: Line) => setLines([...draft.lines.filter((x) => !(inUnit(unitOf(l), l) && inUnit(unitOf(l), x)
    && (l.team === null || x.team === null) && !read.lines.some((r) => r.key === x.key))), l])
  // What HR gives that is taken away shows on its chip above, struck, with Undo (owner, 2 Nov 2026);
  // the table holds the rest: what is added, and a take-away no chip shows exactly (HR no longer
  // gives it, it was kept from an earlier company, or it names a team in one unit, which the chip,
  // a team in any unit, cannot say). A saved take-away undone this edit gives something back, so it
  // stays in the table as Removed until saved (review, 2 Nov 2026).
  const onChip = (l: Line) => l.effect === 'remove' && !l.old && page.placement.some((x) => !!x.d365Code
    && (l.team === null ? x.kind === 'UNIT' && x.d365Code === l.unit : l.unit === null && x.kind === 'TEAM' && x.d365Code === l.team))
  const tableRows: ScopeRow[] = rows.filter(({ line, mark }) => mark === 'removed' || !onChip(line)).map(({ line: l, mark }) => ({
    key: l.key, unit: l.unit, team: l.team, mark,
    status: (
      <>
        {l.effect === 'remove' && <Badge>Taken away from HR</Badge>}
        {l.old && <Badge tone="warn">Kept from their earlier company {l.company}: gives nothing now</Badge>}
        {l.effect === 'add' && l.paused && !l.resume && <Badge tone="warn">Paused {day(l.paused)}</Badge>}
      </>
    ),
    actions: mark === 'removed' ? (
      <Button size="sm" variant="ghost" onClick={() => restore(l)}>Undo</Button>
    ) : (
      <>
        {l.effect === 'add' && l.paused && !l.old && (
          <Button size="sm" variant="ghost" onClick={() => setLines(draft.lines.map((x) => (x === l ? { ...x, resume: !x.resume } : x)))}>
            {l.resume ? 'Keep paused' : 'Resume'}
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => setLines(draft.lines.filter((x) => x !== l))}>Remove line</Button>
      </>
    ),
  }))

  return (
    <>
      {notice && <Notice tone={notice.tone} onDismiss={() => setNotice(null)}>{notice.text}</Notice>}

      {/* "HR Main Scope", "From HR" on the right, the company its own row (owner, 1 Nov 2026). Each ✕
          says what it does in its tooltip; the header's hint went with the company. */}
      <Panel
        title="HR Main Scope"
        aside={<span className="text-(length:--text-small) font-medium text-(--color-ink)">From HR</span>}
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="eyebrow w-32">Company</span>
            {p.compId || p.entityName ? (
              <span className="text-(length:--text-small) text-(--color-ink)">
                {p.entityName && <span className="font-medium">{p.entityName}</span>}
                {p.compId && <span className="text-(--color-ink-3)">{p.entityName ? ' · ' : ''}{p.compId}</span>}
              </span>
            ) : <span className="text-(length:--text-small) text-(--color-ink-4)">none</span>}
          </div>
          {(['UNIT', 'TEAM'] as const).map((kind) => (
            <div key={kind} className="flex flex-wrap items-center gap-2">
              <span className="eyebrow w-32">{kind === 'UNIT' ? 'Business Units' : 'Business Teams'}</span>
              {page.placement.filter((x) => x.kind === kind).map((x) => {
                const gone = x.d365Code ? removedChip(kind, x.d365Code) : undefined
                return (
                  <PlaceChip key={`${x.mindzerId}-${x.d365Code ?? ''}`} name={x.name ?? `#${x.mindzerId}`} code={x.d365Code} struck={!!gone}>
                    {x.d365Code && !gone && (
                      <button type="button" title="Take away for this user" aria-label={`Take ${x.name ?? x.d365Code} away for this user`}
                        onClick={() => removeChip(kind, x.d365Code!)}
                        className="-mr-1 grid h-5 w-5 place-items-center rounded-full text-(--color-ink-3) hover:bg-(--color-overdue)/10 hover:text-(--color-overdue)">✕</button>
                    )}
                    {gone && (
                      <button type="button" onClick={() => setLines(draft.lines.filter((l) => l !== gone))}
                        className="font-medium text-(--color-navy-700) no-underline hover:underline">Undo</button>
                    )}
                  </PlaceChip>
                )
              })}
              {page.placement.every((x) => x.kind !== kind) && <span className="text-(length:--text-small) text-(--color-ink-4)">none</span>}
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Manual Override HR Main Scope">
        {/* Laid out as the additional scope card's Unit and Team (owner, 29 Oct 2026), each a multi-select. */}
        {codes && codes.length > 0 && (
          <div className="mb-3 flex flex-col gap-2">
            <div className="flex flex-wrap items-end gap-3">
              <FilterSlot label="Business Unit">
                <MultiSearchSelect label="Business Unit" noun="Business Units" emptyText="No Business Unit added" panelWidth={340} keepCase
                  value={unitsPicked} onChange={setUnits} options={unitOptions} />
              </FilterSlot>
              <FilterSlot label="Business Team">
                {unitsPicked.length === 0
                  ? <span className="flex h-8 items-center text-(length:--text-small) text-(--color-ink-4)">Pick a Business Unit first</span>
                  : teamOptions.length === 0
                    ? <span className="flex h-8 items-center text-(length:--text-small) text-(--color-ink-4)">No Business Teams in {unitsPicked.length === 1 ? 'this Business Unit' : 'these Business Units'}</span>
                    : (
                      <MultiSearchSelect label="Business Team" noun="Business Teams" panelWidth={340} keepCase
                        emptyText={unitsPicked.length === 1 && unitsPicked[0] === NO_UNIT ? 'Pick its Business Teams'
                          : unitsPicked.length === 1 ? 'All Business Teams in this Business Unit' : 'All Business Teams in these Business Units'}
                        value={teamsPicked} onChange={setTeams} options={teamOptions} groups={teamGroups} />
                    )}
              </FilterSlot>
            </div>
            {noUnitTeamless && (
              <p className="text-(length:--text-small) text-(--color-ink-3)">No Business Unit: pick its Business Teams.</p>
            )}
          </div>
        )}
        {tableRows.length === 0 ? (
          <p className="text-(length:--text-small) text-(--color-ink-3)">No Business Units or Business Teams added.</p>
        ) : <ScopeTable rows={tableRows} unitName={unitName} teamName={teamName} actions />}
      </Panel>

      <Panel title="Override HR Role Configuration" bodyClassName="!px-0 !py-0">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-(length:--text-small)">
            <thead>
              <tr className="bg-(--color-surface-2) text-left text-(--color-ink-3)">
                <th className="px-4 py-2 font-medium">Setting</th>
                <th className="px-4 py-2 font-medium">The HR Role says</th>
                <th className="px-4 py-2 font-medium">For this user</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={3} className="border-t border-(--color-line) bg-(--color-surface-2)/60 px-4 pt-2.5 pb-1">
                  <span className="eyebrow">Reports</span>
                </td>
              </tr>
              {Object.keys(draft.reports).map((code) => (
                <SettingRow key={`reports-${code}`} label={label(code)}
                  roleSays={!roleRead ? null : roleSays('reports', code) ? 'On' : 'Off'}
                  changed={draft.reports[code] !== read.reports[code]}
                  mark={markOf(read.reports[code], draft.reports[code], onOff(() => roleSays('reports', code)))}>
                  <Segmented label={label(code)} value={draft.reports[code]} onChange={(v) => setReport(code, v)}
                    options={[{ value: 'role', label: 'Same as role' }, { value: 'on', label: 'On' }, { value: 'off', label: 'Off' }]} />
                </SettingRow>
              ))}
            </tbody>
            {/* Each main-scope report's own column groups (D53). Until the role is read,
                which reports are theirs is not known, so none is listed. */}
            {roleFailed && (
              <tbody>
                <tr>
                  <td colSpan={3} className="border-t border-(--color-line) px-4 py-2.5 text-(--color-warn)">
                    Their HR Role could not be read, so their column groups cannot be shown. Reload the page to set them.
                  </td>
                </tr>
              </tbody>
            )}
            {groupsPlaced && Object.keys(draft.reports).filter(reportOn).map((slug) => {
              const codes = Object.keys(draft.groups).filter((c) => groupInfo(c)?.reportSlug === slug)
              return (
                <tbody key={`groups-${slug}`}>
                  <tr>
                    <td colSpan={3} className="border-t border-(--color-line) bg-(--color-surface-2)/60 px-4 pt-2.5 pb-1">
                      <span className="eyebrow">{reportLabel(slug)} column groups</span>
                    </td>
                  </tr>
                  {codes.length === 0 ? (
                    <tr className="border-t border-(--color-line)">
                      <td colSpan={3} className="px-4 py-2 text-(--color-ink-3)">None yet: {reportLabel(slug)}&apos;s columns show as set on Column definition.</td>
                    </tr>
                  ) : codes.map((code) => (
                    <SettingRow key={`groups-${code}`} label={label(code)}
                      roleSays={roleSays('groups', code) ? 'On' : 'Off'}
                      changed={draft.groups[code] !== read.groups[code]}
                      mark={markOf(read.groups[code], draft.groups[code], onOff(() => roleSays('groups', code)))}>
                      <Segmented label={label(code)} value={draft.groups[code]} onChange={(v) => setDraft((d) => ({ ...d, groups: { ...d.groups, [code]: v } }))}
                        options={[{ value: 'role', label: 'Same as role' }, { value: 'on', label: 'On' }, { value: 'off', label: 'Off' }]} />
                    </SettingRow>
                  ))}
                </tbody>
              )
            })}
            {groupsPlaced && Object.keys(draft.groups).some((c) => draft.groups[c] !== 'role' && !reportOn(groupInfo(c)?.reportSlug ?? '')) && (
              <tbody>
                <tr>
                  <td colSpan={3} className="border-t border-(--color-line) bg-(--color-surface-2)/60 px-4 pt-2.5 pb-1">
                    <span className="eyebrow">Kept from a report no longer in their main scope</span>
                  </td>
                </tr>
                {Object.keys(draft.groups).filter((c) => draft.groups[c] !== 'role' && !reportOn(groupInfo(c)?.reportSlug ?? '')).map((code) => (
                  <SettingRow key={`inert-${code}`} label={`${reportLabel(groupInfo(code)?.reportSlug ?? '')}: ${label(code)}`}
                    roleSays={roleSays('groups', code) ? 'On' : 'Off'} changed={draft.groups[code] !== read.groups[code]}
                    mark={markOf(read.groups[code], draft.groups[code], onOff(() => roleSays('groups', code)))}>
                    <span className="text-(--color-ink-2)">{draft.groups[code] === 'on' ? 'On' : 'Off'}</span>
                    <Badge tone="warn">No effect now</Badge>
                    <Button size="sm" variant="ghost" onClick={() => setDraft((d) => ({ ...d, groups: { ...d.groups, [code]: 'role' } }))}>Clear it</Button>
                  </SettingRow>
                ))}
              </tbody>
            )}
            <tbody>
              <tr>
                <td colSpan={3} className="border-t border-(--color-line) bg-(--color-surface-2)/60 px-4 pt-2.5 pb-1">
                  <span className="eyebrow">How they see it</span>
                </td>
              </tr>
              <SettingRow label="Hierarchy" roleSays={!roleRead ? null : role?.role?.setUp ? HIERARCHY[role.role.hierarchyMode ?? 0] : 'Off'}
                changed={draft.hierarchy !== read.hierarchy} mark={markOf(read.hierarchy, draft.hierarchy, hierarchyLevel)}>
                <Segmented label="Hierarchy" value={draft.hierarchy} onChange={(v) => setDraft((d) => ({ ...d, hierarchy: v }))}
                  options={[{ value: 'role', label: 'Same as role' }, { value: 'off', label: 'Off' }, { value: 'type1', label: 'Type 1' }, { value: 'type2', label: 'Type 2' }]} />
              </SettingRow>
              <SettingRow label="Phone" roleSays={!roleRead ? null : role?.role?.setUp && role.role.mobileAllowed ? 'On' : 'Off'}
                changed={draft.phone !== read.phone}
                mark={markOf(read.phone, draft.phone, onOff(() => !!role?.role?.setUp && !!role.role.mobileAllowed))}>
                <Segmented label="Phone" value={draft.phone} onChange={(v) => setDraft((d) => ({ ...d, phone: v }))}
                  options={[{ value: 'role', label: 'Same as role' }, { value: 'on', label: 'On' }, { value: 'off', label: 'Off' }]} />
              </SettingRow>
            </tbody>
          </table>
        </div>
      </Panel>

      {/* Every edit undone after a review is no change, not "changed since the review" (review, 1 Nov 2026). */}
      <EffectFooter sticky changed={changed} reviewed={reviewed} effect={changed ? effect?.answer ?? null : null} reason={reason} setReason={setReason}
        busy={busy} onReview={review} onSave={save} onDiscard={() => { setDraft(read); setEffect(null); setReason('') }} saveLabel="Save changes" />
    </>
  )
}

/**
 * What a change to one of these settings does, by what the person ends up with, "Same as role"
 * counting as what the role gives (owner, 29 Oct 2026: Added or Removed, not "changed"). A change
 * that leaves it as it was, such as On where the role already says On, says so. Before the role is
 * read nothing is said: what it gives is not known yet.
 */
type SettingMark = 'added' | 'removed' | 'same' | null
function markOf(before: string, after: string, level: (v: string) => number | null): SettingMark {
  if (before === after) return null
  const b = level(before)
  const a = level(after)
  if (a === null || b === null) return null
  return a === b ? 'same' : a > b ? 'added' : 'removed'
}

/** A Business Unit or Business Team by its Mindzer name, code in grey, as the HR placement chips and
 *  the pickers show it; the code alone where it has no name: the HR main scope and the additional
 *  scope tables (1 Nov 2026). */
function CodeName({ code, name }: { code: string; name?: string | null }) {
  return name && name !== code
    ? <span className="text-(--color-ink)">{name}<span className="text-(--color-ink-3)"> · {code}</span></span>
    : <span className="text-(--color-ink)">{code}</span>
}

/** One row of a scope table: its unit and team, what this edit does to it, and anything else to say or do. */
type ScopeRow = { key: string; unit: string | null; team: string | null; mark?: 'added' | 'removed'; status?: ReactNode; actions?: ReactNode }

/**
 * THE SCOPE TABLES, ONE DESIGN (owner, 2 Nov 2026): HR main scope's and MBI additional scope's
 * lines as Business Team and Change under a divider naming each Business Unit (always shown: it is
 * the only place the unit is named, the owner having dropped the Business Unit column as the
 * divider says it), a line between the columns and the row under the pointer lit. Change says only
 * what this edit does, Added or Removed (struck through until saved); a line saved and untouched
 * says no change, only what it is (paused, taken away from HR, kept from an earlier company).
 */
function ScopeTable({ rows, unitName, teamName, actions = false }: {
  rows: ScopeRow[]; unitName: (code: string) => string | null | undefined; teamName: (code: string) => string | null | undefined; actions?: boolean
}) {
  // Each unit's rows together, the units in the order first met. With no unit, a team line is in
  // any unit and a line with no team is every unit: two groups, so each divider says which.
  const ANY = '\u0001any', ALL = '\u0001all'
  const groupOf = (r: ScopeRow) => r.unit ?? (r.team ? ANY : ALL)
  const units = [...rows.reduce((m, r) => m.set(groupOf(r), [...(m.get(groupOf(r)) ?? []), r]), new Map<string, ScopeRow[]>())]
  const cell = 'whitespace-nowrap px-4 py-2'
  const split = 'border-l border-(--color-line)'
  return (
    <div className="overflow-x-auto rounded-md border border-(--color-line)">
      <table className="w-full border-collapse text-(length:--text-small)">
        <thead>
          <tr className="bg-(--color-surface-2) text-left text-(--color-ink-3)">
            <th className={`${cell} font-medium`}>Business Team</th>
            <th className={`${cell} ${split} font-medium`}>Change</th>
            {actions && <th className={`${cell} ${split}`}><span className="sr-only">Actions</span></th>}
          </tr>
        </thead>
        {units.map(([unit, its]) => {
          const name = unit === ANY || unit === ALL ? undefined : unitName(unit)
          return (
            <tbody key={unit}>
              <tr>
                <td colSpan={actions ? 3 : 2} className="border-t border-(--color-line) bg-(--color-surface-2) px-4 pt-2.5 pb-1">
                  <span className="eyebrow">{unit === ANY ? 'Any Business Unit' : unit === ALL ? 'All Business Units'
                    : name && name !== unit ? `${name} · ${unit}` : unit}</span>
                </td>
              </tr>
              {its.map((r) => {
                // Struck cell by cell: a struck row would strike its Removed badge too.
                const struck = r.mark === 'removed' ? 'line-through decoration-(--color-ink-4)' : ''
                return (
                  <tr key={r.key} className="border-t border-(--color-line) transition-colors hover:bg-(--color-navy-50)">
                    <td className={`${cell} ${struck}`}>
                      {r.team ? <CodeName code={r.team} name={teamName(r.team)} /> : <span className="text-(--color-ink-3)">All Business Teams</span>}
                    </td>
                    {/* Wraps: a long badge must not push the row's buttons out of view (review, 2 Nov 2026). */}
                    <td className={`px-4 py-2 ${split}`}>
                      <span className="flex flex-wrap items-center gap-2">
                        {r.mark === 'added' && <Badge tone="good">Added</Badge>}
                        {r.mark === 'removed' && <Badge tone="warn">Removed</Badge>}
                        {r.status}
                      </span>
                    </td>
                    {actions && <td className={`${cell} ${split} text-right`}>{r.actions}</td>}
                  </tr>
                )
              })}
            </tbody>
          )
        })}
      </table>
    </div>
  )
}

/** One setting: its name, what the HR role says, and this person's choice, marked when changed. */
function SettingRow({ label, roleSays, changed, mark, children }: {
  label: string; roleSays: string | null; changed: boolean; mark: SettingMark; children: ReactNode
}) {
  return (
    <tr className={`border-t border-(--color-line) transition-colors ${changed ? 'bg-(--color-navy-50)' : 'hover:bg-(--color-surface-2)'}`}>
      <td className="whitespace-nowrap px-4 py-2 font-medium text-(--color-ink)">{label}</td>
      <td className="whitespace-nowrap px-4 py-2">
        {roleSays === null ? <span className="text-(--color-ink-4)">…</span> : (
          <span className={`inline-flex h-5 items-center rounded-full px-2 text-(length:--text-micro) font-semibold ${
            roleSays === 'Off' ? 'bg-(--color-surface-3) text-(--color-ink-3)' : 'bg-(--color-good)/10 text-(--color-good)'}`}>{roleSays}</span>
        )}
      </td>
      <td className="px-4 py-2">
        <span className="flex flex-wrap items-center gap-2">
          {children}
          {changed && mark === 'added' && <ChangedMark on />}
          {changed && mark === 'removed' && <ChangedMark on={false} />}
          {changed && mark === 'same' && <Badge>No change in access</Badge>}
        </span>
      </td>
    </tr>
  )
}

/* =========================================================================
   EXTRA SCOPES: other companies only
   ========================================================================= */

export function ExtraScopesTab({ api, page, onSaved }: { api: Api; page: AccessPersonPage; onSaved: (text: string) => void }) {
  const [adding, setAdding] = useState(false)
  const reports = useMemo(() => uniqueOf(page, 'REPORT'), [page])
  const groups = useMemo(() => uniqueOf(page, 'GROUP'), [page])
  const main = page.person?.compId ?? null
  // One card per scope: its units and teams are lines sharing a scope group (owner, 29 Oct 2026).
  const scopes = useMemo(() => [...extraScopesOf(page).values()], [page])
  return (
    <>
      <Panel
        title={`MBI additional scope${scopes.length ? ` · ${scopes.length}` : ''}`}
        aside={!adding ? <Button variant="primary" size="sm" onClick={() => setAdding(true)}>+ Add additional scope</Button> : undefined}
      >
        <p className="text-(length:--text-small) leading-relaxed text-(--color-ink-2)">
          Reports and their column groups in <span className="font-semibold text-(--color-ink)">other companies</span>, on top of their main company{main ? ` ${main}` : ''}.
          {' '}Business Units and Business Teams in {main ?? 'their main company'} are set under HR main scope.
        </p>
        {page.extras.length === 0 && !adding && (
          <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-(--color-line-strong) px-4 py-6 text-center">
            <span className="text-(length:--text-base) font-semibold text-(--color-ink)">No additional scopes</span>
            <span className="text-(length:--text-small) text-(--color-ink-3)">They see only their main company{main ? `, ${main}` : ''}.</span>
          </div>
        )}
      </Panel>
      {adding && (
        <ExtraScopeCard api={api} page={page} lines={null} reports={reports} groups={groups}
          onDone={(text) => { setAdding(false); onSaved(text) }} onCancel={() => setAdding(false)} />
      )}
      {scopes.map((rows) => (
        // Keyed by its lines and versions: a save draws the card afresh from what was saved.
        <ExtraScopeCard key={rows.map((r) => `${r.extraScopeId}:${r.version ?? ''}`).join(',')} api={api} page={page} lines={rows}
          reports={reports} groups={groups} onDone={onSaved} />
      ))}
    </>
  )
}

function uniqueOf(page: AccessPersonPage, kind: 'REPORT' | 'GROUP') {
  return [...new Map(page.matrix.filter((c) => c.kind === kind).map((c) => [c.code, { label: c.label, reportSlug: c.reportSlug }])).entries()]
    .map(([code, x]) => ({ code, label: x.label, reportSlug: x.reportSlug }))
}

/**
 * What each of these groups meant when this page read it (MATRIX), by ID. A save sends it
 * back, and a group it newly gives that was renamed or given other columns since is refused
 * (51637; owner, 29 Oct 2026): the page is read again before it can give that group.
 */
function groupKeysOf(page: AccessPersonPage, codes: string[]): Record<string, string> {
  return Object.fromEntries(codes.flatMap((code) => {
    const k = page.matrix.find((c) => c.kind === 'GROUP' && c.code === code)?.groupKey
    return k ? [[code, k]] : []
  }))
}

/**
 * The Team picker's headings once more than one unit is picked: each unit's teams under its name,
 * in the Unit picker's order, saying what that unit gives now (owner, 30 Oct 2026). A unit with
 * none of its teams ticked gives all teams; "No unit" gives nothing until its teams are ticked.
 * Each team option carries its unit's value as `group`. HR main scope also says when a unit has a
 * line for all its teams beside team lines (`whole`, which is what applies) and when its lines
 * are paused.
 */
function teamHeadings(units: SearchSelectOption[], unitsPicked: string[], teams: SearchSelectOption[],
  teamsPicked: string[], noUnit: string, whole: (unit: string) => boolean = () => false,
  paused: (unit: string) => boolean = () => false): SearchSelectGroup[] | undefined {
  if (unitsPicked.length < 2) return undefined
  return units.filter((u) => unitsPicked.includes(u.value)).map((u) => {
    const its = teams.filter((t) => t.group === u.value)
    const ticked = its.filter((t) => teamsPicked.includes(t.value)).length
    const gives = ticked > 0 && !whole(u.value) ? `${ticked} of ${its.length} ticked`
      : u.value === noUnit ? 'pick its Business Teams' : 'all Business Teams'
    return {
      key: u.value,
      label: u.label,
      hint: u.value === noUnit || u.label === u.value ? undefined : u.value,
      note: `${gives}${paused(u.value) ? ' (paused)' : ''}`,
    }
  })
}

// A team picked is its unit and the team, joined; "No unit" holds the teams D365 records under no unit.
const SCOPE_SEP = '\t'
const SCOPE_NO_UNIT = '\u0001no-unit'

/**
 * One additional scope: its lines are its units and teams, sharing one set of reports and
 * column groups (owner, 29 Oct 2026). Picked as on Main scope: units first, then the teams
 * of the units picked; no unit picked is every unit, and a unit with none of its teams
 * picked is the whole unit.
 */
function ExtraScopeCard({ api, page, lines, reports, groups, onDone, onCancel }: {
  api: Api; page: AccessPersonPage; lines: AccessExtraScope[] | null
  reports: { code: string; label: string; reportSlug: string }[]; groups: { code: string; label: string; reportSlug: string }[]
  onDone: (text: string) => void; onCancel?: () => void
}) {
  const main = page.person?.compId ?? null
  // The scope is read as of its newest line, the version the save checks (51603). The API drops a
  // version's trailing zeros ("…:35Z", "…:35.5Z"), so the fraction is padded before comparing.
  const versionKey = (v: string | null) => {
    const [s, f = ''] = (v ?? '').replace(/Z$/, '').split('.')
    return `${s}.${f.padEnd(7, '0')}`
  }
  const scope = lines ? lines.reduce((a, b) => (versionKey(b.version) > versionKey(a.version) ? b : a)) : null
  // The daily re-check pauses line by line, so a scope can be partly paused.
  const pausedLines = (lines ?? []).filter((l) => l.pausedUtc)
  const paused = pausedLines[0] ?? null
  // A report retired or a column group deleted since this scope was saved has no box
  // to untick; sent back, every save would be refused (51614). Dropped, and said so
  // (review, 26 Oct 2026).
  const stored = { reports: scope?.reports ? scope.reports.split(', ') : [], groups: scope?.columnGroupIds ? scope.columnGroupIds.split(', ') : [] }
  const retired = [...stored.reports.filter((c) => !reports.some((r) => r.code === c)),
    ...stored.groups.filter((c) => !groups.some((g) => g.code === c)).map((c) => `column group ${c}`)]
  const unitOfLine = (l: AccessExtraScope) => (l.anyUnit ? SCOPE_NO_UNIT : l.businessUnit ?? '')
  const savedLines = (lines ?? []).filter((l) => !(l.anyUnit && l.anyTeam))
  const initial = {
    company: scope?.compId ?? '',
    units: [...new Set(savedLines.map(unitOfLine))],
    teams: savedLines.filter((l) => !l.anyTeam).map((l) => `${unitOfLine(l)}${SCOPE_SEP}${l.businessTeam ?? ''}`),
    reports: stored.reports.filter((c) => reports.some((r) => r.code === c)),
    groups: stored.groups.filter((c) => groups.some((g) => g.code === c)),
    resume: false,
  }
  const [draft, setDraft] = useState(initial)
  const [companies, setCompanies] = useState<AccessOrgCode[]>([])
  const [codes, setCodes] = useState<AccessOrgCode[]>([])
  const [effect, setEffect] = useState<{ key: string; remove: boolean; answer: AccessPersonEffect } | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<{ tone: 'good' | 'bad'; text: string } | null>(null)

  useEffect(() => {
    if (scope) return
    let cancelled = false
    api.accessOrgCodes().then((c) => !cancelled && setCompanies(c.codes)).catch(() => !cancelled && setCompanies([]))
    return () => {
      cancelled = true
    }
  }, [api, scope])
  useEffect(() => {
    if (!draft.company) return
    let cancelled = false
    api.accessOrgCodes(draft.company).then((c) => !cancelled && setCodes(c.codes)).catch(() => !cancelled && setCodes([]))
    return () => {
      cancelled = true
    }
  }, [api, draft.company])

  // The picks as a set, not in the order clicked: a report unticked and ticked again is no change (review of 19l).
  const keyOf = (d: typeof draft) => JSON.stringify({ ...d, units: [...d.units].sort(), teams: [...d.teams].sort(),
    reports: [...d.reports].sort(), groups: [...d.groups].sort() })
  const key = keyOf(draft)
  const changed = key !== keyOf(initial) || !scope
  const reviewed = !!effect && !effect.remove && effect.key === key

  // The lines the picks give: no unit is every unit; "No unit" gives only its teams picked.
  type ScopeLine = { unit: string | null; team: string | null }
  const linesOf = (units: string[], picked: string[]): ScopeLine[] => units.length === 0 ? [{ unit: null, team: null }]
    : units.flatMap((u): ScopeLine[] => {
      const unit = u === SCOPE_NO_UNIT ? null : u
      const teams = picked.filter((t) => t.startsWith(`${u}${SCOPE_SEP}`)).map((t) => t.slice(u.length + SCOPE_SEP.length))
      return teams.length > 0 ? teams.map((team) => ({ unit, team })) : unit === null ? [] : [{ unit, team: null }]
    })
  const linesNow = linesOf(draft.units, draft.teams)
  // The card's table (the manager, 1 Nov 2026; one design with HR main scope, owner, 2 Nov 2026):
  // what the picks give, one row a line, and what this edit adds and removes (on a new scope, every
  // line is added); a line removed keeps its place, as on HR main scope. "Before" is the lines as
  // saved, not as the pickers rebuild them: a line the
  // pickers cannot hold (every unit beside a unit, a whole unit beside its teams) is dropped by any
  // save, so it shows as removed (review, 1 Nov 2026).
  const lineKey = (l: ScopeLine) => `${l.unit ?? ''}${SCOPE_SEP}${l.team ?? ''}`
  const linesBefore = [...new Map((lines ?? []).map((l): ScopeLine => ({
    unit: l.anyUnit ? null : l.businessUnit ?? null, team: l.anyTeam ? null : l.businessTeam ?? null,
  })).map((l) => [lineKey(l), l])).values()]
  const scopeRows: ScopeRow[] = [
    ...linesBefore.map((b) => ({ ...b, key: lineKey(b), mark: linesNow.some((l) => lineKey(l) === lineKey(b)) ? undefined : 'removed' as const })),
    ...linesNow.filter((l) => !linesBefore.some((b) => lineKey(b) === lineKey(l))).map((l) => ({ ...l, key: lineKey(l), mark: 'added' as const })),
  ]
  // "No Business Unit" with none of its teams gives nothing, and a save drops it (the old summary said so).
  const noUnitTeamless = draft.units.includes(SCOPE_NO_UNIT) && !draft.teams.some((t) => t.startsWith(`${SCOPE_NO_UNIT}${SCOPE_SEP}`))

  const body = (remove: boolean): AccessExtraScopeBody => ({
    expectedVersion: scope?.version ?? null,
    company: draft.company || null,
    unit: null,
    team: null,
    lines: linesNow,
    reports: draft.reports,
    columnGroups: draft.groups.map(Number),
    resume: draft.resume,
    reason: reason.trim() || undefined,
    scopeId: scope?.extraScopeId ?? null,
    remove,
    columnGroupKeys: groupKeysOf(page, draft.groups),
  })

  const review = (remove: boolean) => {
    if (busy || (!remove && (!draft.company || linesNow.length === 0))) return
    if (effect && effect.remove !== remove) setReason('')
    setBusy(true)
    setNotice(null)
    api.accessExtraScopePreview(page.person!.mindzerUserId, body(remove))
      .then((a) => setEffect({ key, remove, answer: a }))
      .catch((e: unknown) => setNotice({ tone: 'bad', text: messageOf(e) }))
      .finally(() => setBusy(false))
  }

  const save = () => {
    if (!effect || busy || !reason.trim()) return
    const id = page.person!.mindzerUserId
    setBusy(true)
    const call = effect.remove ? api.accessExtraScopeRemove(id, scope!.extraScopeId, scope!.version, reason.trim())
      : scope ? api.accessExtraScopeSave(id, scope.extraScopeId, body(false)) : api.accessExtraScopeAdd(id, body(false))
    call
      .then((a) => {
        setEffect(null)
        setReason('')
        onDone(`${effect.remove ? 'Removed the additional scope' : scope ? 'Saved the additional scope' : 'Added the additional scope'}. ${personEffectSentences(a.summary).join(' ')}`)
      })
      .catch((e: unknown) => { setEffect(null); setNotice({ tone: 'bad', text: messageOf(e) }) })
      .finally(() => setBusy(false))
  }

  // A unit unticked takes its teams with it.
  const setUnits = (next: string[]) =>
    setDraft((d) => ({ ...d, units: next, teams: d.teams.filter((t) => next.some((u) => t.startsWith(`${u}${SCOPE_SEP}`))) }))
  const setTeams = (next: string[]) => setDraft((d) => ({ ...d, teams: next }))
  // The pickers' rows, as on Main scope. A value picked that the code list no longer offers is
  // still a row, named by its code, so a trigger never shows a raw key.
  const unitCodes = codes.filter((c) => c.kind === 'UNIT' && c.unitCode)
  const teamCodes = codes.filter((c) => c.kind === 'TEAM' && c.teamCode)
  const unitOptions = [
    ...unitCodes.map((c) => ({ value: c.unitCode!, label: c.mindzerName ?? c.unitCode!, hint: c.unitCode! })),
    ...(teamCodes.some((c) => !c.unitCode) || draft.units.includes(SCOPE_NO_UNIT)
      ? [{ value: SCOPE_NO_UNIT, label: 'No Business Unit', hint: 'Business Teams with no Business Unit' }] : []),
    ...draft.units.filter((u) => u !== SCOPE_NO_UNIT && !unitCodes.some((c) => c.unitCode === u))
      .map((u) => ({ value: u, label: u, hint: `${u} · not in use in D365 now` })),
  ]
  // With more than one unit picked, each team sits under its unit's heading (teamHeadings). A team
  // ticked under "No unit" is saved with no unit, which is every unit, so it says so (review, 30 Oct 2026).
  const teamOptions: SearchSelectOption[] = teamCodes.filter((c) => draft.units.includes(c.unitCode || SCOPE_NO_UNIT)).map((c) => ({
    value: `${c.unitCode || SCOPE_NO_UNIT}${SCOPE_SEP}${c.teamCode}`,
    label: c.mindzerName ?? c.teamCode!,
    hint: `${c.teamCode}${c.unitCode ? '' : ' · in any Business Unit'}`,
    group: c.unitCode || SCOPE_NO_UNIT,
  }))
  for (const t of draft.teams) {
    if (teamOptions.some((o) => o.value === t)) continue
    const [u, team] = t.split(SCOPE_SEP)
    // A team saved for every unit (before units were picked first) sits under "No unit".
    const elsewhere = u === SCOPE_NO_UNIT ? teamCodes.find((c) => c.teamCode === team) : undefined
    teamOptions.push({ value: t, label: elsewhere?.mindzerName ?? team, hint: `${team} · ${elsewhere ? 'in any Business Unit' : 'not in use in D365 now'}`, group: u })
  }
  const teamGroups = teamHeadings(unitOptions, draft.units, teamOptions, draft.teams, SCOPE_NO_UNIT)
  const unitName = (u: string) => unitCodes.find((c) => c.unitCode === u)?.mindzerName
  const teamName = (t: string) => teamCodes.find((c) => c.teamCode === t)?.mindzerName
  // A report taken off takes its column groups with it: a group goes only with its own report (D53, 51627).
  const groupsOf = (slugs: string[], ids: string[]) => ids.filter((c) => slugs.includes(groups.find((g) => g.code === c)?.reportSlug ?? ''))
  const toggleReport = (code: string) =>
    setDraft((d) => {
      const r = d.reports.includes(code) ? d.reports.filter((x) => x !== code) : [...d.reports, code]
      return { ...d, reports: r, groups: groupsOf(r, d.groups) }
    })
  const toggleGroup = (code: string) =>
    setDraft((d) => ({ ...d, groups: d.groups.includes(code) ? d.groups.filter((x) => x !== code) : [...d.groups, code] }))
  const quiet = (text: string) => <span className="flex h-8 items-center text-(length:--text-small) text-(--color-ink-4)">{text}</span>

  return (
    <section className={`flex flex-col rounded-md border bg-(--color-surface) ${scope ? 'border-(--color-line)' : 'border-(--color-navy-300) ring-1 ring-(--color-navy-300)'}`}>
      <div className="flex flex-col gap-3 border-b border-(--color-line) px-4 py-3">
        {scope ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <span className="inline-flex h-7 items-center rounded-md bg-(--color-navy-700) px-2.5 text-(length:--text-small) font-semibold tracking-[0.02em] text-white">{scope.compId}</span>
            {!paused ? <Badge tone="good">In use</Badge>
              : pausedLines.length === lines!.length ? <Badge tone="warn">Paused: {paused.pausedReason ?? 'this is now their main company'}</Badge>
                : <Badge tone="warn">Partly paused ({pausedLines.length} of {lines!.length}): {paused.pausedReason ?? 'this is now their main company'}</Badge>}
            {/* Paused because it is now their main company: resuming would only be refused (51606). */}
            {paused && scope.compId !== main && (
              <ToggleChip on={draft.resume} onChange={(on) => setDraft((d) => ({ ...d, resume: on }))}>Resume it</ToggleChip>
            )}
          </div>
        ) : <span className="eyebrow">New additional scope</span>}
        {/* Unit and Team as on Main scope, each a multi-select (owner, 29 Oct 2026); the company is fixed once saved. */}
        <div className="flex flex-wrap items-end gap-3">
          {!scope && (
            <FilterSlot label="Company" wide>
              <SearchSelect label="Company" value={draft.company} placeholder="Pick a company"
                onChange={(v) => setDraft((d) => (v === main ? d : { ...d, company: v, units: [], teams: [] }))}
                options={[{ value: '', label: 'Pick a company' }, ...companies.map((c) => ({
                  value: c.compId, label: `${c.mindzerName ?? c.compId}`,
                  hint: c.compId === main ? `${c.compId} · their main company: use HR main scope to add Business Units or Business Teams there` : c.compId,
                }))]} />
            </FilterSlot>
          )}
          <FilterSlot label="Business Unit">
            {draft.company
              ? <MultiSearchSelect label="Business Unit" noun="Business Units" emptyText="All Business Units" panelWidth={340} keepCase
                  value={draft.units} onChange={setUnits} options={unitOptions} />
              : quiet('Pick a company first')}
          </FilterSlot>
          <FilterSlot label="Business Team">
            {!draft.company ? quiet('Pick a company first')
              : draft.units.length === 0 ? quiet('All Business Teams: pick a Business Unit to choose')
                : teamOptions.length === 0 ? quiet(`No Business Teams in ${draft.units.length === 1 ? 'this Business Unit' : 'these Business Units'}`)
                  : (
                    <MultiSearchSelect label="Business Team" noun="Business Teams" panelWidth={340} keepCase
                      emptyText={draft.units.length === 1 && draft.units[0] === SCOPE_NO_UNIT ? 'Pick its Business Teams'
                        : draft.units.length === 1 ? 'All Business Teams in this Business Unit' : 'All Business Teams in these Business Units'}
                      value={draft.teams} onChange={setTeams} options={teamOptions} groups={teamGroups} />
                  )}
          </FilterSlot>
        </div>
        {draft.company && scopeRows.length > 0 && <ScopeTable rows={scopeRows} unitName={unitName} teamName={teamName} />}
        {notice && <Notice tone={notice.tone} onDismiss={() => setNotice(null)}>{notice.text}</Notice>}
      </div>

      {/* Reports, each opening on its own column groups when ticked, as on an HR role (owner, 29 Oct 2026). */}
      <div className="px-4 py-3">
        <ReportGroups reports={reports} groups={groups}
          chosenReports={draft.reports} chosenGroups={draft.groups}
          onToggleReport={toggleReport}
          onSetReports={(codes) => setDraft((d) => ({ ...d, reports: codes, groups: groupsOf(codes, d.groups) }))}
          onToggleGroup={toggleGroup}
          onSetGroups={(slug, codes) => setDraft((d) => ({
            ...d, groups: [...d.groups.filter((c) => groups.find((g) => g.code === c)?.reportSlug !== slug), ...codes],
          }))}
          // A saved scope marks what this edit adds or removes; on a new one everything is new.
          reportChanged={scope ? (c) => draft.reports.includes(c) !== initial.reports.includes(c) : undefined}
          groupChanged={scope ? (c) => draft.groups.includes(c) !== initial.groups.includes(c) : undefined} />
      </div>
      {(draft.reports.length === 0 || retired.length > 0 || (!!draft.company && noUnitTeamless)) && (
        <div className="flex flex-col gap-1 px-4 pb-3 text-(length:--text-small)">
          {!!draft.company && noUnitTeamless && <p className="text-(--color-warn)">{linesNow.length === 0
            ? '“No Business Unit” gives nothing on its own: pick its Business Teams, or another Business Unit.'
            : '“No Business Unit” gives nothing without its Business Teams: pick them, or untick it.'}</p>}
          {draft.reports.length === 0 && <p className="text-(--color-warn)">An additional scope gives nothing until a report is on.</p>}
          {retired.length > 0 && <p className="text-(--color-warn)">No longer in MBI, dropped when you save: {retired.join(', ')}.</p>}
        </div>
      )}

      <EffectFooter changed={changed} reviewed={reviewed || !!effect?.remove} effect={effect?.answer ?? null} reason={reason} setReason={setReason}
        busy={busy} onReview={() => review(false)} onSave={save}
        onDiscard={() => { setDraft(initial); setEffect(null); setReason(''); onCancel?.() }}
        discardLabel={scope ? 'Discard' : 'Cancel'}
        saveLabel={effect?.remove ? 'Remove this additional scope' : scope ? 'Save changes' : 'Add additional scope'} danger={!!effect?.remove}
        note={scope?.changedByEmail ? `Last changed by ${scope.changedByEmail}${scope.version ? ` ${day(scope.version)}` : ''}${scope.changeReason ? `: “${scope.changeReason}”` : ''}` : undefined}
        extra={scope && !effect ? <Button variant="danger" onClick={() => review(true)} disabled={busy}>Remove…</Button> : undefined} />
    </section>
  )
}

/* =========================================================================
   The effect, the reason and the save, shared by both tabs
   ========================================================================= */

/**
 * Review, then say why, then save. Under Main scope it is held at the bottom of the
 * page, buttons on the right, so it can be pressed from anywhere in a long list
 * (owner, 27 Oct 2026); on an extra scope's card it closes the card.
 */
function EffectFooter({ changed, reviewed, effect, reason, setReason, busy, onReview, onSave, onDiscard, saveLabel, danger, extra, sticky = false, note, discardLabel = 'Discard' }: {
  changed: boolean; reviewed: boolean; effect: AccessPersonEffect | null; reason: string; setReason: (s: string) => void
  busy: boolean; onReview: () => void; onSave: () => void; onDiscard: () => void; saveLabel: string; danger?: boolean; extra?: ReactNode
  sticky?: boolean; note?: string; discardLabel?: string
}) {
  const s = effect?.summary ?? null
  const hint = !changed && !effect ? 'No changes yet.'
    : effect && reviewed ? 'Give the reason of the change, then save. It is kept in their history.'
      : effect ? 'Changed since the review: review again before saving.'
        : 'Review shows who gains or loses what, before anything is saved.'
  return (
    <div className={`flex flex-col gap-2.5 border-t border-(--color-line) bg-(--color-surface) px-4 py-3 ${
      sticky ? 'sticky bottom-0 z-10 -mx-5 -mb-4 mt-auto px-5 shadow-[0_-6px_16px_-8px_rgba(15,23,42,0.18)]' : 'rounded-b-md'}`}>
      {effect && (
        <div className={`flex flex-col gap-1 rounded-md border px-3 py-2 text-(length:--text-small) ${
          !reviewed ? 'border-(--color-overdue)/30 bg-(--color-overdue)/5'
            : s && (s.Widens === true || s.Widens === 1) ? 'border-(--color-warn)/30 bg-(--color-warn)/6'
              : 'border-(--color-line) bg-(--color-surface-2)'}`}>
          <span className="eyebrow">What this does</span>
          {/* Capped: held at the bottom of Main scope, a long list would cover the settings above it. */}
          <ul className="scroll-slim flex max-h-28 flex-col gap-0.5 overflow-y-auto text-(--color-ink-2)">{personEffectSentences(s).map((l, i) => <li key={i}>{l}</li>)}</ul>
          {s && (s.Widens === true || s.Widens === 1) ? <p className="font-semibold text-(--color-warn)">⚠ This widens access.</p>
            : s && s.People_Narrowed > 0 ? <p className="font-semibold text-(--color-ink)">This narrows access.</p> : null}
        </div>
      )}
      {effect && reviewed && (
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Reason Of The Change (required, kept in their history)"
          className="rounded-md border border-(--color-line-strong) bg-(--color-surface) px-2.5 py-1.5 text-(length:--text-small) text-(--color-ink)" />
      )}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <span className={`mr-auto text-(length:--text-small) ${effect && !reviewed ? 'font-medium text-(--color-overdue)' : 'text-(--color-ink-3)'}`}>
          {note && !effect ? note : hint}
        </span>
        {extra}
        {(changed || effect) && <Button variant="ghost" onClick={onDiscard}>{discardLabel}</Button>}
        {effect && reviewed ? (
          <Button variant={danger ? 'danger' : 'primary'} onClick={onSave} disabled={busy || !reason.trim()}>{saveLabel}</Button>
        ) : (
          <Button variant="primary" onClick={onReview} disabled={busy || !changed}>Review changes</Button>
        )}
      </div>
    </div>
  )
}

/** One person's change as sentences, no more than the counts mean. */
export function personEffectSentences(s: AccessPersonEffectSummary | null): string[] {
  if (!s) return []
  const out: string[] = []
  const n = (k: number, one: string, many: string) => `${k} ${k === 1 ? one : many}`
  const yes = (v: unknown) => v === true || v === 1
  if (yes(s.Can_Enter_After) && !yes(s.Can_Enter_Before)) out.push('They can enter MBI after this.')
  if (!yes(s.Can_Enter_After) && yes(s.Can_Enter_Before)) out.push('They can no longer enter MBI after this.')
  if (s.Reports_Gained > 0) out.push(`${n(s.Reports_Gained, 'report opening', 'report openings')} gained (theirs, and any managers' above them).`)
  if (s.Reports_Lost > 0) out.push(`${n(s.Reports_Lost, 'report opening', 'report openings')} taken away.`)
  if (s.Companies_Gained > 0) out.push(`${n(s.Companies_Gained, 'user reaches', 'users reach')} a company they did not before.`)
  if (s.Column_Groups_Gained > 0) out.push(`${n(s.Column_Groups_Gained, 'user gains', 'users gain')} a column group.`)
  if (s.Managers_Affected > 0) out.push(`${n(s.Managers_Affected, 'manager', 'managers')} above them (hierarchy type 2) ${s.Managers_Affected === 1 ? 'is' : 'are'} affected too.`)
  if (s.Hierarchy_Before != null && s.Hierarchy_After != null && s.Hierarchy_Before !== s.Hierarchy_After)
    out.push(`Hierarchy: ${HIERARCHY[s.Hierarchy_Before] ?? '?'} → ${HIERARCHY[s.Hierarchy_After] ?? '?'}.`)
  if (s.Phone_Before != null && s.Phone_After != null && yes(s.Phone_Before) !== yes(s.Phone_After))
    out.push(`Phone: ${yes(s.Phone_After) ? 'allowed' : 'not allowed'} from now.`)
  if (s.Lines_Resumed) out.push(`${n(s.Lines_Resumed, 'paused line resumes', 'paused lines resume')}.`)
  if (yes(s.Paused)) out.push('It stays paused: the company is now their main company.')
  // The builder lists only people whose access changes: a count above 0 means rows move.
  if (s.People_Widened > 0) out.push(`${n(s.People_Widened, 'user sees', 'users see')} more than before.`)
  if (s.People_Narrowed > 0) out.push(`${n(s.People_Narrowed, 'user sees', 'users see')} less than before.`)
  if (out.length === 0) out.push(s.People_Changed === 0 && !s.Edits ? 'No change: nobody’s access moves.'
    : s.People_Changed === 0 ? 'Their settings change; nobody’s rows move.' : `${n(s.People_Changed, 'user’s access changes', 'users’ access changes')}.`)
  return out
}
